import { todayISO } from "./format";

export const SPEC_FIELDS = ["quantity", "dimensions", "material", "color", "artwork"];

export const emptySpecRow = () => ({ quantity: "", dimensions: "", material: "", color: "", artwork: "" });

export const newJobForm = ({ jobNo = "", currency = "", preparedBy = "" } = {}) => ({
  job_no: jobNo,
  order_date: todayISO(),
  customer_name: "",
  contact_person: "",
  mobile: "",
  email: "",
  billing_address: "",
  same_as_billing: false,
  delivery_address: "",
  store_customer_id: null,
  services: [],
  service_other: "",
  job_description: "",
  items: [emptySpecRow()],
  deadline_date: todayISO(),
  deadline_time: "",
  currency,
  total_price: "",
  advance_paid: "",
  payment_method: "cash",
  payment_other: "",
  special_instructions: "",
  prepared_by: preparedBy,
  assigned_to: "",
  status: "pending",
  approval_date: todayISO(),
  customer_signature: "",
  recipient_name: "",
  recipient_type: "delivered",
  recipient_date: "",
  recipient_signature: "",
});

const text = (value) => (value === null || value === undefined ? "" : String(value));

export const jobToForm = (job) => {
  const form = newJobForm();
  Object.keys(form).forEach((key) => {
    if (key in job) form[key] = job[key];
  });
  [
    "job_no", "order_date", "customer_name", "contact_person", "mobile", "email", "billing_address",
    "delivery_address", "service_other", "job_description", "deadline_date", "currency", "payment_other",
    "special_instructions", "prepared_by", "assigned_to", "approval_date", "customer_signature",
    "recipient_name", "recipient_date", "recipient_signature",
  ].forEach((key) => {
    form[key] = text(job[key]);
  });
  form.deadline_time = text(job.deadline_time).slice(0, 5);
  form.total_price = text(job.total_price);
  form.advance_paid = text(job.advance_paid);
  form.same_as_billing = !!job.same_as_billing;
  form.services = Array.isArray(job.services) ? job.services : [];
  form.items = Array.isArray(job.items) && job.items.length
    ? job.items.map((row) => ({ ...emptySpecRow(), ...row }))
    : [emptySpecRow()];
  return form;
};

export const calculateBalance = (form) =>
  (parseFloat(form.total_price) || 0) - (parseFloat(form.advance_paid) || 0);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Returns { field, message } for the first problem on that step, or null. */
export const validateStep = (step, form) => {
  if (step === 1) {
    if (!form.job_no.trim()) return { field: "job_no", message: "Please enter a valid Job Order Number" };
    if (!form.order_date) return { field: "order_date", message: "Please select an order date" };
    if (!form.customer_name.trim()) return { field: "customer_name", message: "Please enter the Customer / Company Name" };
    if (!form.mobile.trim()) return { field: "mobile", message: "Please enter a mobile contact number" };
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) return { field: "email", message: "Please enter a valid email address" };
  }
  if (step === 3) {
    const total = parseFloat(form.total_price);
    if (form.total_price === "" || Number.isNaN(total) || total < 0) {
      return { field: "total_price", message: "Please specify a valid Total Price / Amount" };
    }
    const advance = parseFloat(form.advance_paid);
    if (form.advance_paid !== "" && (Number.isNaN(advance) || advance < 0)) {
      return { field: "advance_paid", message: "Please specify a valid Advance Paid amount" };
    }
    if (!Number.isNaN(advance) && advance > total) {
      return { field: "advance_paid", message: "Advance paid cannot be more than the total price" };
    }
  }
  return null;
};

export const validateAll = (form) => {
  for (let step = 1; step <= 4; step++) {
    const error = validateStep(step, form);
    if (error) return { ...error, step };
  }
  return null;
};

const STEP_OF_FIELD = {
  job_no: 1, order_date: 1, customer_name: 1, contact_person: 1, mobile: 1, email: 1,
  billing_address: 1, same_as_billing: 1, delivery_address: 1, store_customer_id: 1,
  services: 2, service_other: 2, job_description: 2, items: 2, deadline_date: 2, deadline_time: 2,
  currency: 3, total_price: 3, advance_paid: 3, payment_method: 3, payment_other: 3, special_instructions: 3,
};

/* Which wizard step a server-side (422) error field lives on; e.g. "items.0.material" → 2. */
export const stepForField = (field) => STEP_OF_FIELD[String(field).split(".")[0]] || 4;

export const toPayload = (form) => ({
  ...form,
  job_no: form.job_no.trim(),
  customer_name: form.customer_name.trim(),
  mobile: form.mobile.trim(),
  email: form.email.trim() || null,
  total_price: parseFloat(form.total_price) || 0,
  advance_paid: parseFloat(form.advance_paid) || 0,
  deadline_date: form.deadline_date || null,
  deadline_time: form.deadline_time || null,
  approval_date: form.approval_date || null,
  recipient_date: form.recipient_date || null,
  items: form.items
    .map((row) => SPEC_FIELDS.reduce((acc, key) => ({ ...acc, [key]: String(row[key] || "").trim() }), {}))
    .filter((row) => SPEC_FIELDS.some((key) => row[key] !== "")),
});
